import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../components/CustomSelect';
import { analytics } from '../analytics';

const plannerFilterOptions = [
  { value: 'all', label: 'All' },
  { value: 'assigned', label: 'Assigned (Locked)' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'conflict', label: 'Keep Away' }
];

function matchesPlannerFilter(student, filterValue) {
  switch (filterValue) {
    case 'assigned':
      return Boolean(student.assignedClassId);
    case 'unassigned':
      return !student.assignedClassId;
    case 'conflict':
      return (student.keepAway ?? []).length > 0;
    default:
      return true;
  }
}

export default function OverviewView({
  dashboard,
  filteredStudents,
  filteredClasses,
  availableGrades,
  selectedGrade,
  onGradeChange,
  filterOptions,
  activeFilter,
  onFilterChange,
  onGenerateClick,
  isGenerating,
  terminology,
  onUpdateProjectSettings,
  permissions,
  onAssignStudent,
  onToggleLock,
  onToggleBehavior,
  onApplyRunAssignments,
  latestCompletedRun
}) {
  const navigate = useNavigate();
  const [alertFilter, setAlertFilter] = useState(null);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('table');
  const [plannerFilter, setPlannerFilter] = useState('all');
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedDiffIds, setSelectedDiffIds] = useState(new Set());
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const flashTimeoutRef = useRef(null);
  const [flashStudentId, setFlashStudentId] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showLockHelp, setShowLockHelp] = useState(false);
  const activeGeneration = dashboard.activeGeneration;

  // --- Sorting ---

  function handleSortClick(column) {
    if (sortColumn === column) {
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  // --- Filtering ---

  const preFilteredStudents = alertFilter
    ? filteredStudents.filter((s) => alertFilter.studentIds.includes(s.studentId))
    : filteredStudents;

  const plannerFilteredStudents = preFilteredStudents.filter((s) =>
    matchesPlannerFilter(s, plannerFilter)
  );

  const displayedStudents = useMemo(() => {
    return [...plannerFilteredStudents].sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortColumn === 'assigned') {
        cmp = (a.assignedClassName || 'Unassigned').localeCompare(
          b.assignedClassName || 'Unassigned'
        );
      } else if (sortColumn === 'grade') {
        cmp = (a.gradeLevel || '').localeCompare(b.gradeLevel || '');
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [plannerFilteredStudents, sortColumn, sortDirection]);

  // --- Assignment lookup for smart relationship chips ---
  const assignmentById = useMemo(() => {
    const map = {};
    for (const s of filteredStudents) {
      if (s.assignedClassId) map[s.id] = s.assignedClassId;
    }
    return map;
  }, [filteredStudents]);

  // --- Friend satisfaction stat ---
  const friendMatchStat = useMemo(() => {
    const withFriends = filteredStudents.filter((s) => (s.friendRequests ?? []).length > 0);
    if (withFriends.length === 0) return { total: 0, matched: 0, pct: 0 };
    const matched = withFriends.filter((s) => {
      if (!s.assignedClassId) return false;
      return (s.friendRequests ?? []).some((fr) => fr.id && assignmentById[fr.id] === s.assignedClassId);
    }).length;
    return { total: withFriends.length, matched, pct: Math.round((matched / withFriends.length) * 100) };
  }, [filteredStudents, assignmentById]);

  // --- Class stats derived from assignedClassId ---

  // Use server-computed class stats directly — they're already accurate
  const classStats = filteredClasses;

  // --- Diff for solver results modal ---

  const diffRows = useMemo(() => {
    if (!latestCompletedRun?.assignments) return [];
    const studentMap = new Map(filteredStudents.map((s) => [s.id, s]));
    const classMap = new Map(filteredClasses.map((c) => [c.id, c.name]));
    return latestCompletedRun.assignments
      .map((a) => {
        const student = studentMap.get(a.studentId);
        if (!student) return null;
        const currentClassId = student.assignedClassId || null;
        const proposedClassId = a.classId || null;
        if (currentClassId === proposedClassId) return null;
        return {
          studentId: a.studentId,
          studentName: student.name,
          currentClass: student.assignedClassName || 'Unassigned',
          proposedClass: classMap.get(a.classId) || 'Unassigned'
        };
      })
      .filter(Boolean);
  }, [latestCompletedRun, filteredStudents, filteredClasses]);

  useEffect(() => {
    if (showResultsModal && diffRows.length > 0) {
      setSelectedDiffIds(new Set(diffRows.map((r) => r.studentId)));
    }
  }, [showResultsModal, diffRows]);

  // --- Flash row helper ---

  function flashRow(studentId) {
    const row = document.getElementById(`planner-row-${studentId}`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
    }
    setFlashStudentId(studentId);
    flashTimeoutRef.current = setTimeout(() => {
      setFlashStudentId(null);
      flashTimeoutRef.current = null;
    }, 1800);
  }

  // --- Assignment handler ---

  async function handleAssignStudent(studentId, classId) {
    await onAssignStudent(studentId, classId);
    flashRow(studentId);
  }

  async function handleToggleLock(studentId, isLocked) {
    await onToggleLock(studentId, isLocked);
  }

  // --- Drag and drop ---

  function handleDragStart(event, studentId) {
    event.dataTransfer.setData('text/plain', studentId);
    event.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(event, columnId) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverColumnId !== columnId) {
      setDragOverColumnId(columnId);
    }
  }

  function handleDragLeave(event, columnId) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      if (dragOverColumnId === columnId) {
        setDragOverColumnId(null);
      }
    }
  }

  async function handleDrop(event, columnClassId) {
    event.preventDefault();
    setDragOverColumnId(null);
    const studentId = event.dataTransfer.getData('text/plain');
    if (studentId) {
      await handleAssignStudent(studentId, columnClassId);
    }
  }

  // --- Results modal ---

  function handleSelectAllDiff() {
    setSelectedDiffIds(new Set(diffRows.map((r) => r.studentId)));
  }

  function handleDeselectAllDiff() {
    setSelectedDiffIds(new Set());
  }

  function handleToggleDiffRow(studentId) {
    setSelectedDiffIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }

  async function handleApplySelected() {
    if (selectedDiffIds.size === 0 || !latestCompletedRun) return;
    await onApplyRunAssignments(latestCompletedRun.id, Array.from(selectedDiffIds));
    setShowResultsModal(false);
  }

  // --- Student quick-view popup ---

  function handleOpenStudentPopup(student) {
    setEditingStudent(student);
  }

  function handleCloseStudentPopup() {
    setEditingStudent(null);
  }

  // --- Export ---

  function handleExportPlannerCSV() {
    if (!filteredStudents.length) return;

    const headers = [
      'Name',
      'Student ID',
      'Grade',
      'Gender',
      `Assigned ${terminology.class}`,
      'Lock Status',
      'Behavior',
      'Friend Requests',
      'Keep Away'
    ];

    const escapeCSV = (val) => {
      const str = String(val ?? '');
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const rows = filteredStudents.map((student) => [
      escapeCSV(student.name),
      escapeCSV(student.studentId),
      escapeCSV(student.gradeLevel),
      escapeCSV(student.gender),
      escapeCSV(student.assignedClassName || 'Unassigned'),
      escapeCSV(student.isLocked ? 'Locked' : 'Unlocked'),
      escapeCSV(student.behaviorTag || 'Steady'),
      escapeCSV((student.friendRequests ?? []).map((fr) => fr.name ?? fr).join(', ')),
      escapeCSV((student.keepAway ?? []).map((ka) => ka.name ?? ka).join(', '))
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = (dashboard.project.name || 'planner').replace(/[^a-zA-Z0-9_-]/g, '_');
    link.setAttribute('download', `${safeName}-assignments.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    analytics.track('csv_exported');
  }

  // --- Generation button ---

  const generationButtonLabel = isGenerating
    ? 'Starting proposal'
    : activeGeneration
      ? activeGeneration.statusKey === 'queued'
        ? 'Solver queued'
        : 'Solver running'
      : 'Run Solver';

  // --- Board view helpers ---

  const boardColumns = useMemo(() => {
    const unassigned = plannerFilteredStudents.filter((s) => !s.assignedClassId);
    const columns = [
      {
        id: null,
        name: 'Unassigned',
        capacity: null,
        students: unassigned
      }
    ];
    for (const stat of classStats) {
      const students = plannerFilteredStudents.filter(
        (s) => s.assignedClassId === stat.id
      );
      columns.push({
        id: stat.id,
        name: stat.name,
        capacity: stat.capacity,
        students
      });
    }
    return columns;
  }, [classStats, plannerFilteredStudents]);

  // --- Sort indicator ---

  function sortIndicator(column) {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  // --- Class options for dropdowns ---

  function classOptionsForStudent(student) {
    const opts = [{ value: '', label: 'Unassigned' }];
    const gradeClasses = filteredClasses.filter(
      (c) => c.gradeLevel === student.gradeLevel
    );
    for (const c of gradeClasses) {
      opts.push({ value: c.id, label: c.name });
    }
    return opts;
  }

  // --- Planner filter counts ---

  const plannerFilterCounts = useMemo(() => {
    const counts = {};
    for (const opt of plannerFilterOptions) {
      counts[opt.value] = preFilteredStudents.filter((s) =>
        matchesPlannerFilter(s, opt.value)
      ).length;
    }
    return counts;
  }, [preFilteredStudents]);

  return (
    <>
      {/* Hero section */}
      <section className="hero-card">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="eyebrow-copy">
              {dashboard.project.sourceGrade ? `Grade ${dashboard.project.sourceGrade} to Grade ${dashboard.project.targetGrade}` : dashboard.project.schoolYear || ''}
            </span>
          </div>
          <h1>{dashboard.project.name}</h1>
          <p>{dashboard.project.summary}</p>
        </div>

        <div className="hero-actions">
          <button
            type="button"
            className="utility-button"
            onClick={handleExportPlannerCSV}
            disabled={filteredStudents.length === 0}
          >
            Export CSV
          </button>
          {latestCompletedRun ? (
            <button
              type="button"
              className="utility-button"
              onClick={() => setShowResultsModal(true)}
            >
              View latest results
            </button>
          ) : null}
          <button
            type="button"
            className="primary-button"
            onClick={onGenerateClick}
            disabled={isGenerating || Boolean(activeGeneration)}
          >
            {isGenerating ? <span className="button-spinner" aria-hidden="true" /> : null}
            {generationButtonLabel}
          </button>
        </div>
      </section>

      {/* Grade selector */}
      {availableGrades.length > 1 ? (
        <div className="grade-selector-strip">
          <span className="grade-selector-label">Select Grade</span>
          {availableGrades.map((grade) => (
            <button
              key={grade.name}
              type="button"
              className={`grade-selector-chip ${selectedGrade === grade.name ? 'is-active' : ''}`}
              onClick={() => onGradeChange(grade.name)}
            >
              {grade.name}
              <span className="grade-selector-count">{grade.studentCount}</span>
            </button>
          ))}
        </div>
      ) : null}

      {/* Alert banner */}
      {dashboard.alerts.length > 0 && (
        <section className="alert-banner">
          <button
            type="button"
            className="alert-banner-toggle"
            onClick={() => setAlertsExpanded((prev) => !prev)}
          >
            <span className={`alert-banner-chevron ${alertsExpanded ? 'is-open' : ''}`}>&#9654;</span>
            <span className="alert-banner-summary">
              {(() => {
                const dangerCount = dashboard.alerts.filter((a) => a.tone === 'danger').length;
                const warnCount = dashboard.alerts.filter((a) => a.tone === 'warning').length;
                const accentCount = dashboard.alerts.filter((a) => a.tone === 'accent').length;
                const neutralCount = dashboard.alerts.filter((a) => a.tone === 'neutral').length;
                const goodOnly = dashboard.alerts.length === 1 && dashboard.alerts[0].tone === 'good';
                if (goodOnly) {
                  return (
                    <span className="alert-banner-tag good">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.3 5.3l-4 4a.5.5 0 01-.7 0l-2-2a.5.5 0 11.7-.7L7 9.3l3.6-3.6a.5.5 0 11.7.7z" fill="currentColor"/></svg>
                      {dashboard.alerts[0].title}
                    </span>
                  );
                }
                return (
                  <>
                    {dangerCount > 0 && (
                      <span className="alert-banner-tag danger">
                        <img src={`${process.env.PUBLIC_URL}/caution.png`} alt="" className="alert-banner-icon" />
                        {dangerCount} critical
                      </span>
                    )}
                    {warnCount > 0 && (
                      <span className="alert-banner-tag warning">
                        <img src={`${process.env.PUBLIC_URL}/caution.png`} alt="" className="alert-banner-icon" />
                        {warnCount} warning{warnCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {accentCount > 0 && (
                      <span className="alert-banner-tag accent">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M8 4.5v4M8 10.5v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        {accentCount} note{accentCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {neutralCount > 0 && (
                      <span className="alert-banner-tag neutral">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M8 4.5v4M8 10.5v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        {neutralCount} info
                      </span>
                    )}
                  </>
                );
              })()}
            </span>
          </button>
          {alertsExpanded && (
            <ul className="alert-banner-list">
              {[...dashboard.alerts].sort((a, b) => {
                const order = { danger: 0, warning: 1, accent: 2, neutral: 3, good: 4 };
                return (order[a.tone] ?? 9) - (order[b.tone] ?? 9);
              }).map((alert) => (
                <li key={alert.title} className={`alert-banner-item ${alert.tone}`}>
                  <span className="alert-banner-item-icon">
                    {(alert.tone === 'danger' || alert.tone === 'warning') && (
                      <img src={`${process.env.PUBLIC_URL}/caution.png`} alt="" className="alert-banner-icon" />
                    )}
                    {alert.tone === 'good' && (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.3 5.3l-4 4a.5.5 0 01-.7 0l-2-2a.5.5 0 11.7-.7L7 9.3l3.6-3.6a.5.5 0 11.7.7z" fill="currentColor"/></svg>
                    )}
                    {(alert.tone === 'accent' || alert.tone === 'neutral') && (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M8 4.5v4M8 10.5v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    )}
                  </span>
                  <div className="alert-banner-item-text">
                    <strong>{alert.title}</strong>
                    <span className="alert-banner-desc">{alert.description}</span>
                    {alert.action ? (
                      <button
                        type="button"
                        className={`utility-button alert-action-button ${alertFilter?.filterKey === alert.action.filterKey ? 'is-active' : ''}`}
                        onClick={() =>
                          setAlertFilter(
                            alertFilter?.filterKey === alert.action.filterKey ? null : alert.action
                          )
                        }
                      >
                        {alertFilter?.filterKey === alert.action.filterKey ? 'Remove filter' : alert.action.label}
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Donut strip + planner wrapped so sticky works within grid */}
      <div className="planner-sticky-region">
      {classStats.length > 0 && (
      <section className="class-donut-strip">
        {/* Friend match summary card */}
        {friendMatchStat.total > 0 && (() => {
          const pct = friendMatchStat.pct;
          const r = 36;
          const c = 2 * Math.PI * r;
          const filledLen = (pct / 100) * c;
          return (
          <article className="class-donut-card friend-match-card">
            <span className="class-donut-name">Friend Match</span>
            <div className="class-donut-ring-wrap">
              <svg viewBox="0 0 80 80" className="class-donut-svg">
                <circle cx="40" cy="40" r={r} fill="none" stroke="#eef1f5" strokeWidth="7" />
                <circle cx="40" cy="40" r={r} fill="none" stroke="#68d391" strokeWidth="7"
                  strokeDasharray={`${filledLen} ${c - filledLen}`}
                  strokeDashoffset={0}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                />
              </svg>
              <div className="class-donut-center">
                <span className="class-donut-count">{pct}%</span>
              </div>
            </div>
            <div className="class-donut-legend">
              <span className="class-donut-legend-item">{friendMatchStat.matched} / {friendMatchStat.total}</span>
            </div>
          </article>
          );
        })()}
        {classStats.map((classroom) => {
          const isOver = classroom.filledSeats > classroom.capacity;
          const total = Math.max(classroom.capacity, classroom.filledSeats) || 1;
          const girlsFrac = classroom.girls / total;
          const boysFrac = classroom.boys / total;
          const otherFrac = Math.max(0, classroom.filledSeats - classroom.girls - classroom.boys) / total;
          const r = 36;
          const c = 2 * Math.PI * r;
          const girlsLen = girlsFrac * c;
          const boysLen = boysFrac * c;
          const otherLen = otherFrac * c;
          const girlsOffset = 0;
          const boysOffset = -girlsLen;
          const otherOffset = -(girlsLen + boysLen);

          return (
          <article key={classroom.id} className={`class-donut-card ${isOver ? 'is-over' : ''}`}>
            <span className="class-donut-name">{classroom.name}</span>
            <div className="class-donut-ring-wrap">
              <svg viewBox="0 0 80 80" className="class-donut-svg">
                <circle cx="40" cy="40" r={r} fill="none" stroke="#eef1f5" strokeWidth="7" />
                {isOver ? (
                  <circle cx="40" cy="40" r={r} fill="none" stroke="var(--danger, #e53e3e)" strokeWidth="7" strokeLinecap="round" />
                ) : (
                  <>
                    {classroom.girls > 0 ? (
                      <circle cx="40" cy="40" r={r} fill="none" stroke="#f0a0be" strokeWidth="7"
                        strokeDasharray={`${girlsLen} ${c - girlsLen}`}
                        strokeDashoffset={girlsOffset}
                        transform="rotate(-90 40 40)"
                      />
                    ) : null}
                    {classroom.boys > 0 ? (
                      <circle cx="40" cy="40" r={r} fill="none" stroke="#8eaff0" strokeWidth="7"
                        strokeDasharray={`${boysLen} ${c - boysLen}`}
                        strokeDashoffset={boysOffset}
                        transform="rotate(-90 40 40)"
                      />
                    ) : null}
                    {otherFrac > 0 ? (
                      <circle cx="40" cy="40" r={r} fill="none" stroke="#b0b8c4" strokeWidth="7"
                        strokeDasharray={`${otherLen} ${c - otherLen}`}
                        strokeDashoffset={otherOffset}
                        transform="rotate(-90 40 40)"
                      />
                    ) : null}
                  </>
                )}
              </svg>
              <div className="class-donut-center">
                <span className={`class-donut-count ${isOver ? 'is-over' : ''}`}>{classroom.filledSeats}</span>
                <span className="class-donut-cap">/ {classroom.capacity}</span>
              </div>
            </div>
            <div className="class-donut-legend">
              <span className="class-donut-legend-item"><span className="class-donut-dot" style={{ background: '#f0a0be' }} />{classroom.girls} Girls</span>
              <span className="class-donut-legend-item"><span className="class-donut-dot" style={{ background: '#8eaff0' }} />{classroom.boys} Boys</span>
              {classroom.behaviorFlags > 0 ? (
                <span className="class-donut-behavior">{classroom.behaviorFlags} behavior</span>
              ) : null}
            </div>
          </article>
          );
        })}
      </section>
      )}

      <div className="explanation-banner">
        <strong>How the planner works</strong>
        <p>
          This is the source of truth for {terminology.class.toLowerCase()} assignments. Assign {terminology.students.toLowerCase()} manually using the table or board view, or click <strong>Run Solver</strong> for AI-powered placement recommendations.
        </p>
      </div>

      {/* View mode toggle + planner panel */}
      <section className="panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">{terminology.student} assignments</span>
            <h2>Planner</h2>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              type="button"
              className={`filter-chip view-toggle ${viewMode === 'table' ? 'is-active' : ''}`}
              onClick={() => { setViewMode('table'); analytics.track('view_mode_changed', { mode: 'table' }); }}
            >
              Table
            </button>
            <button
              type="button"
              className={`filter-chip view-toggle ${viewMode === 'board' ? 'is-active' : ''}`}
              onClick={() => { setViewMode('board'); analytics.track('view_mode_changed', { mode: 'board' }); }}
            >
              Board
            </button>
          </div>
        </div>

        {/* Planner filter chips */}
        <div className="chip-row" style={{ marginBottom: '0.75rem' }}>
          {plannerFilterOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip ${plannerFilter === opt.value ? 'is-active' : ''}`}
              onClick={() => setPlannerFilter(opt.value)}
            >
              {opt.label}
              <span style={{ marginLeft: '0.25rem', opacity: 0.7 }}>{plannerFilterCounts[opt.value]}</span>
            </button>
          ))}
        </div>

        {alertFilter ? (
          <div className="alert-filter-banner">
            <span>Filtered: {alertFilter.studentIds.length} {terminology.students.toLowerCase()} shown</span>
            <button type="button" className="utility-button" onClick={() => setAlertFilter(null)}>
              Clear filter
            </button>
          </div>
        ) : null}

        <div className="sub-toolbar">
          <div className="toolbar-meta">{displayedStudents.length} visible {terminology.students.toLowerCase()}</div>
        </div>

        {/* Table view */}
        {viewMode === 'table' ? (
          <div className="table-wrap">
            <table className="data-grid planner-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSortClick('name')}>
                    {terminology.student}{sortIndicator('name')}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortClick('grade')}>
                    Grade{sortIndicator('grade')}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortClick('assigned')}>
                    Assigned {terminology.class}{sortIndicator('assigned')}
                  </th>
                  <th>Lock <button type="button" className="help-icon-btn" onClick={(e) => { e.stopPropagation(); setShowLockHelp(true); }} aria-label="What is lock?">?</button></th>
                  <th>Friends</th>
                  <th>Keep away</th>
                  <th>Behavior</th>
                </tr>
              </thead>
              <tbody>
                {displayedStudents.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--muted)' }}>
                      No {terminology.students.toLowerCase()} to show
                    </td>
                  </tr>
                )}
                {displayedStudents.map((student) => (
                  <tr
                    key={student.id}
                    id={`planner-row-${student.id}`}
                    className={flashStudentId === student.id ? 'flash-row' : ''}
                  >
                    <td>
                      <div
                        className="student-cell planner-student-link"
                        onClick={(e) => { e.stopPropagation(); handleOpenStudentPopup(student); }}
                      >
                        <strong>{student.name}</strong>
                        <span>
                          {student.studentId} {student.gender ? `\u2022 ${student.gender}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>{student.gradeLevel}</td>
                    <td className="planner-assign-cell" onClick={(e) => e.stopPropagation()}>
                      <CustomSelect
                        value={student.assignedClassId || ''}
                        options={classOptionsForStudent(student)}
                        onChange={(newValue) =>
                          handleAssignStudent(student.id, newValue || null)
                        }
                        valueClassName={!student.assignedClassId ? 'is-unassigned' : ''}
                      />
                    </td>
                    <td>
                      {student.assignedClassId ? (
                        <span className="planner-lock-chip is-locked">
                          Locked to {terminology.class}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <div className="relationship-chip-row">
                        {(student.friendRequests ?? []).length === 0
                          ? null
                          : student.friendRequests.map((fr) => {
                              const friendSatisfied = student.assignedClassId && fr.id && assignmentById[fr.id] === student.assignedClassId;
                              return (
                                <span
                                  key={fr.id ?? fr.name}
                                  className={`relationship-chip relationship-chip-good relationship-chip-clickable${friendSatisfied ? ' relationship-chip-satisfied' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); if (fr.id) flashRow(fr.id); }}
                                >
                                  {friendSatisfied ? '✓ ' : ''}{fr.name}
                                </span>
                              );
                            })
                        }
                      </div>
                    </td>
                    <td>
                      <div className="relationship-chip-row">
                        {(student.keepAway ?? []).length === 0
                          ? null
                          : student.keepAway.map((ka) => {
                              const keepAwaySatisfied = student.assignedClassId && ka.id && assignmentById[ka.id] && assignmentById[ka.id] !== student.assignedClassId;
                              return (
                                <span
                                  key={ka.id ?? ka.name}
                                  className={`relationship-chip relationship-chip-keep-away relationship-chip-clickable${keepAwaySatisfied ? ' relationship-chip-ka-satisfied' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); if (ka.id) flashRow(ka.id); }}
                                >
                                  {keepAwaySatisfied ? '✓ ' : ''}{ka.name}
                                </span>
                              );
                            })
                        }
                      </div>
                    </td>
                    <td>
                      {student.behaviorTag && student.behaviorTag !== 'None' ? (
                        <span className={`behavior-pill ${student.behaviorTag.toLowerCase()}`}>
                          {student.behaviorTag}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Board view */}
        {viewMode === 'board' ? (
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              overflowX: 'auto',
              padding: '1rem 0',
              minHeight: '400px',
              alignItems: 'flex-start'
            }}
          >
            {boardColumns.map((column) => {
              const columnKey = column.id ?? '__unassigned__';
              const isDragOver = dragOverColumnId === columnKey;
              return (
                <div
                  key={columnKey}
                  onDragOver={(e) => handleDragOver(e, columnKey)}
                  onDragLeave={(e) => handleDragLeave(e, columnKey)}
                  onDrop={(e) => handleDrop(e, column.id)}
                  style={{
                    minWidth: '220px',
                    maxWidth: '280px',
                    flex: '0 0 auto',
                    background: isDragOver
                      ? 'var(--surface-highlight, rgba(79, 70, 229, 0.08))'
                      : 'var(--surface-raised, #f9fafb)',
                    borderRadius: '0.5rem',
                    padding: '0.75rem',
                    border: isDragOver
                      ? '2px dashed var(--accent, #4f46e5)'
                      : '2px solid transparent',
                    transition: 'background 150ms, border-color 150ms'
                  }}
                >
                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong style={{ fontSize: '0.875rem' }}>{column.name}</strong>
                    <span
                      style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.75rem',
                        opacity: 0.7
                      }}
                    >
                      {column.students.length}{column.capacity != null ? `/${column.capacity}` : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {column.students.map((student) => (
                      <div
                        key={student.id}
                        draggable="true"
                        onDragStart={(e) => handleDragStart(e, student.id)}
                        style={{
                          background: 'var(--surface-default, #fff)',
                          borderRadius: '0.375rem',
                          padding: '0.5rem 0.625rem',
                          cursor: 'grab',
                          fontSize: '0.8125rem',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          border: '1px solid var(--border-light, #e5e7eb)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{student.name}</div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                            {student.gender ? student.gender.charAt(0) : ''}
                            {student.behaviorTag && student.behaviorTag !== 'None'
                              ? ` \u2022 ${student.behaviorTag}`
                              : ''}
                          </div>
                        </div>
                        {student.isLocked ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="#d97706" strokeWidth="1.5" style={{ flexShrink: 0 }} aria-label="Locked">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" />
                          </svg>
                        ) : null}
                      </div>
                    ))}
                    {column.students.length === 0 ? (
                      <div
                        style={{
                          padding: '1.5rem 0.5rem',
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          opacity: 0.4
                        }}
                      >
                        Drop {terminology.students.toLowerCase()} here
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </section>
      </div>{/* end planner-sticky-region */}

      {/* Solver Results Modal */}
      {showResultsModal ? (
        <div className="dialog-overlay" onClick={() => setShowResultsModal(false)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px', width: '100%' }}>
            <div className="panel-head">
              <div>
                <span className="panel-kicker">Solver results</span>
                <h2>{latestCompletedRun?.name || 'Latest run'}</h2>
              </div>
              <button
                type="button"
                className="utility-button"
                onClick={() => setShowResultsModal(false)}
              >
                Close
              </button>
            </div>

            {diffRows.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', opacity: 0.6 }}>
                No changes proposed. All assignments match the current state.
              </div>
            ) : (
              <>
                <div style={{ padding: '0.75rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button type="button" className="utility-button" onClick={handleSelectAllDiff}>
                    Select all
                  </button>
                  <button type="button" className="utility-button" onClick={handleDeselectAllDiff}>
                    Deselect all
                  </button>
                  <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', opacity: 0.7 }}>
                    {selectedDiffIds.size} of {diffRows.length} selected
                  </span>
                </div>

                <div className="table-wrap" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="data-grid">
                    <thead>
                      <tr>
                        <th style={{ width: '2.5rem' }}></th>
                        <th>{terminology.student}</th>
                        <th>Current {terminology.class}</th>
                        <th>Proposed {terminology.class}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffRows.map((row) => (
                        <tr key={row.studentId}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedDiffIds.has(row.studentId)}
                              onChange={() => handleToggleDiffRow(row.studentId)}
                            />
                          </td>
                          <td><strong>{row.studentName}</strong></td>
                          <td>{row.currentClass}</td>
                          <td>{row.proposedClass}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ padding: '0.75rem 1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="utility-button"
                    onClick={() => setShowResultsModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={selectedDiffIds.size === 0}
                    onClick={handleApplySelected}
                  >
                    Apply selected ({selectedDiffIds.size})
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {/* Student Quick-View Popup */}
      {editingStudent ? (
        <div className="dialog-overlay" onClick={handleCloseStudentPopup}>
          <div className="dialog-panel planner-student-popup" onClick={(e) => e.stopPropagation()}>
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">{editingStudent.studentId} · Grade {editingStudent.gradeLevel} · {editingStudent.gender || '—'}</span>
                <h2>{editingStudent.name}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseStudentPopup}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="planner-popup-body">
              <div className="users-form-grid">
                <label className="auth-field">
                  <span>Assigned {terminology.class}</span>
                  <div className="planner-assign-cell" onClick={(e) => e.stopPropagation()}>
                    <CustomSelect
                      value={editingStudent.assignedClassId || ''}
                      options={classOptionsForStudent(editingStudent)}
                      onChange={(newValue) => {
                        handleAssignStudent(editingStudent.id, newValue || null);
                        setEditingStudent((prev) => prev ? { ...prev, assignedClassId: newValue || null, assignedClassName: newValue ? (filteredClasses.find((c) => c.id === newValue)?.name ?? 'Unknown') : 'Unassigned', isLocked: newValue != null } : null);
                      }}
                    />
                  </div>
                </label>
              </div>

              <div className="planner-popup-row-group">
                <div className="planner-popup-row">
                  <span className="planner-popup-row-label">Lock status</span>
                  {editingStudent.assignedClassId ? (
                    <span className="planner-lock-chip is-locked">
                      Locked to {terminology.class}
                    </span>
                  ) : <span className="planner-popup-muted">Not assigned</span>}
                </div>
                <div className="planner-popup-row">
                  <span className="planner-popup-row-label">Behavior</span>
                  <button
                    type="button"
                    className={`tone-pill ${editingStudent.behaviorTag === 'Monitor' ? 'warning' : 'good'}`}
                    style={{ cursor: 'pointer', border: 'none' }}
                    onClick={() => {
                      const newFlag = editingStudent.behaviorTag !== 'Monitor';
                      onToggleBehavior(editingStudent.id, newFlag);
                      setEditingStudent((prev) => prev ? { ...prev, behaviorTag: newFlag ? 'Monitor' : 'Steady' } : null);
                    }}
                  >
                    {editingStudent.behaviorTag || 'Steady'}
                  </button>
                </div>
              </div>

              {((editingStudent.friendRequests ?? []).length > 0 || (editingStudent.keepAway ?? []).length > 0) ? (
                <div className="planner-popup-row-group">
                  {(editingStudent.friendRequests ?? []).length > 0 ? (
                    <div className="planner-popup-row" style={{ alignItems: 'flex-start' }}>
                      <span className="planner-popup-row-label">Friends</span>
                      <div className="planner-popup-chips">
                        {editingStudent.friendRequests.map((fr) => (
                          <span key={fr.id ?? fr.name ?? fr} className="relationship-chip relationship-chip-good">{fr.name ?? fr}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {(editingStudent.keepAway ?? []).length > 0 ? (
                    <div className="planner-popup-row" style={{ alignItems: 'flex-start' }}>
                      <span className="planner-popup-row-label">Keep away</span>
                      <div className="planner-popup-chips">
                        {editingStudent.keepAway.map((ka) => (
                          <span key={ka.id ?? ka.name ?? ka} className="relationship-chip relationship-chip-keep-away">{ka.name ?? ka}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="planner-popup-footer">
              <button
                type="button"
                className="utility-button"
                onClick={() => { handleCloseStudentPopup(); navigate('/roster'); }}
              >
                Edit in {terminology.students}
              </button>
              <button type="button" className="utility-button" onClick={handleCloseStudentPopup}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showLockHelp ? (
        <div className="dialog-overlay" onClick={() => setShowLockHelp(false)}>
          <div className="dialog-panel panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Help</span>
                <h2>How locking works</h2>
              </div>
              <button type="button" className="icon-button classes-dialog-close" onClick={() => setShowLockHelp(false)} aria-label="Close">&times;</button>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text)' }}>
              <p style={{ marginBottom: 12 }}>When you <strong>assign</strong> a {terminology.student.toLowerCase()} to a {terminology.class.toLowerCase()}, that {terminology.student.toLowerCase()} is automatically locked in place. The solver will not move them under any circumstances.</p>
              <p style={{ marginBottom: 12 }}>This is useful when a placement has already been confirmed by a parent or administrator and should not be changed by future solver runs.</p>
              <p style={{ marginBottom: 0 }}><strong>Unassigned</strong> {terminology.students.toLowerCase()} can be freely placed by the solver to optimize overall balance. <strong>Assigned</strong> {terminology.students.toLowerCase()} are treated as fixed constraints.</p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
